import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { useWebSocket } from "../ws/websocket";
import LoadingSpinner from "../common/loading";
import { useEventEmitter } from "../ws/event-context";
import InstructionInput from "../assistant/instruction-input";
import { useBackdrop } from "../common/backdrop";
import UserChatCard from "./user-chat-card";
import AssistantChatCard from "./assistant-chat-card";
import { complete, stt } from "../assistant/assistant-service";
import { useRecoilState } from "recoil";
import { userInfoState } from "../global-state";
import useApiCall from "../common/api-call";
import { useTheme } from "@mui/material/styles";
import { useToast } from "../common/toast";
import { init } from "./chat-service";
import { v4 as uuidv4 } from "uuid";

const ChatView = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assistantEventMessage, setAssistantEventMessage] = useState("");
  const [chatId, setChatId] = useState(null);
  const { ws, sendAndWaitWs } = useWebSocket();
  const { subscribe } = useEventEmitter();
  const { apiCall } = useApiCall();
  const [userInfo] = useRecoilState(userInfoState);
  const [currentContext, setCurrentContext] = useState(null);
  const [nextContext, setNextContext] = useState(null);
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);
  const { successToast, warningToast, errorToast } = useToast();
  const theme = useTheme();

  useEffect(() => {
    const { unsubscribe } = subscribe((target, data) => {
      if (target === "/chat") {
        handleChatReply(data.payload);
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      handleInit();
    }
  }, [ws, ws?.readyState]);

  const handleInit = async () => {
    const resp = await init(sendAndWaitWs, userInfo.activeScope.id);
    if (resp) {
      setChatId(resp.chatId);
      setLoading(false);
    }
  };

  const handleAutomationSave = (sid, aid, atype) => {
    setMessages((prevMessages) => {
      let updatedMessages = [...prevMessages];
      let existingMessageIndex = updatedMessages.findLastIndex(
        (prevMessage) =>
          prevMessage.from === "assistant" &&
          prevMessage.payload.type === "AUTOMATION" &&
          prevMessage.payload.data &&
          prevMessage.payload.data.sessionId === sid
      );
      if (existingMessageIndex !== -1) {
        updatedMessages[existingMessageIndex] = {
          from: "assistant",
          payload: {
            ...updatedMessages[existingMessageIndex].payload,
            content: null,
            data: { sessionId: sid, automationId: aid, automationType: atype },
          },
        };
      }
      return updatedMessages;
    });
  };

  const handleAutomationDelete = (aid) => {
    setMessages((prevMessages) => {
      let updatedMessages = [...prevMessages];
      let existingMessageIndex = updatedMessages.findLastIndex(
        (prevMessage) =>
          prevMessage.from === "assistant" &&
          prevMessage.payload.type === "AUTOMATION" &&
          prevMessage.payload.data &&
          prevMessage.payload.data.automationId === aid
      );
      if (existingMessageIndex !== -1) {
        const n = { ...updatedMessages[existingMessageIndex] };
        const np = { ...n.payload };
        np.content = null;
        np.data.automationId = null;
        np.data.automationType = null;
        np.data.autoRun = null;
        n.payload = np;
        updatedMessages[existingMessageIndex] = n;
      }
      return updatedMessages;
    });
  };

  const handleChatReply = (message) => {
    const newMessage = {
      from: "assistant",
      payload: {
        type: "TEXT",
        content: message,
      },
    };
    setMessages((prevMessages) => {
      let updatedMessages = [...prevMessages];
      updatedMessages.push(newMessage);
      return updatedMessages;
    });
  };

  const handleChangeSummary = (summary) => {
    setNextContext({ ...nextContext, automationDescription: summary });
  };

  const handleReplay = async () => {
    console.log(currentContext);
    await handleSendMessage(currentContext.instruction, currentContext, null);
  };

  const handleSendMessage = async (instruction, context, audioBlob) => {
    const newMessage = {
      from: "user",
      text: audioBlob ? "Voice:" : instruction,
    };
    setMessages((prevMessages) => {
      let updatedMessages = [...prevMessages];
      updatedMessages.push(newMessage);
      return updatedMessages;
    });

    let ins = instruction;
    if (audioBlob) {
      ins = await stt(apiCall, audioBlob);
      if (ins) {
        console.log(ins);
        const voiceMessage = {
          from: "user",
          text: "Voice: " + ins,
        };
        setMessages((prevMessages) => {
          let updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = voiceMessage;
          return updatedMessages;
        });
      } else {
        const voiceMessage = {
          from: "user",
          text: "Voice error!",
        };
        setMessages((prevMessages) => {
          let updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = voiceMessage;
          return updatedMessages;
        });
        errorToast("Error!");
        return;
      }
    }
    setIsWaitingResponse(true);
    // TODO: provide context messages
    const packContext = { ...(context || {}), instruction: ins };

    const id = uuidv4();
    const { unsubscribe } = subscribe((target, data) => {
      if (target === "/assistant/event") {
        const payload = JSON.parse(data.payload);
        if (payload.requestId === id) {
          setAssistantEventMessage(payload.message);
        }
      }
    });
    const resp = await complete(
      sendAndWaitWs,
      id,
      userInfo.activeScope.id,
      chatId || "",
      userInfo.activeScope.untrusted,
      packContext,
      userInfo.setting.debugMode,
      "UI_CHAT",
      true
    );
    unsubscribe();
    setIsWaitingResponse(false);

    if (resp && resp.messages) {
      console.log(resp);
      setCurrentContext(resp.currentContext);
      setNextContext(resp.nextContext);
      setMessages((prevMessages) => {
        let updatedMessages = [...prevMessages];
        resp.messages.forEach((newMessage) => {
          let existingMessageIndex = -1;
          if (newMessage.type === "AUTOMATION") {
            existingMessageIndex = updatedMessages.findLastIndex(
              (prevMessage) =>
                prevMessage.from === "assistant" &&
                prevMessage.payload.type === "AUTOMATION" &&
                prevMessage.payload.data &&
                newMessage.data &&
                prevMessage.payload.data.sessionId === newMessage.data.sessionId
            );
          }
          if (existingMessageIndex !== -1) {
            updatedMessages[existingMessageIndex] = {
              from: "assistant",
              payload: {
                type: "AUTOMATION",
                content: null,
                data: {
                  sessionId: newMessage.data.sessionId,
                  automationId: null,
                },
              },
            };
          }
          updatedMessages.push({
            from: "assistant",
            payload: newMessage,
          });
        });

        return updatedMessages;
      });
    } else {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          from: "assistant",
          payload: {
            type: "ERROR",
            content: "Assistant Error.",
          },
          timestamp: new Date(),
        },
      ]);
    }
  };

  const handleNewChat = async () => {
    setMessages([]);
    setCurrentContext(null);
    setNextContext(null);
    setAssistantEventMessage("");
    setIsWaitingResponse(false);

    const resp = await init(sendAndWaitWs, userInfo.activeScope.id);
    if (resp) {
      setChatId(resp.chatId);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "absolute", // Make it absolute relative to the parent flex container
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pt: {
          xs: "56px", // Mobile AppBar height
          sm: "64px", // Desktop AppBar height
        },
        pb: {
          xs: "80px",
        },
        bgcolor: theme.palette.background.default,
      }}
    >
      {loading ? (
        <></>
      ) : (
        <>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              px: 2,
              pb: "80px", // Space for input bar
              height: "100%",
              position: "relative",
            }}
          >
            {messages.map((message, index) =>
              message.from === "user" ? (
                <UserChatCard key={index} text={message.text} />
              ) : (
                <AssistantChatCard
                  key={index}
                  payload={message.payload}
                  onSave={handleAutomationSave}
                  onReplay={handleReplay}
                  onDelete={handleAutomationDelete}
                  onChangeSummary={handleChangeSummary}
                />
              )
            )}
            {isWaitingResponse && (
              <AssistantChatCard
                payload={{
                  type: "WAITING",
                  message: assistantEventMessage,
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.background.paper,
              borderTop: 1,
              borderColor: "divider",
              zIndex: 1200,
            }}
          >
            <InstructionInput
              placeholder="Instruction here..."
              onSendText={(text) => handleSendMessage(text, nextContext, null)}
              onSendAudio={(audio) =>
                handleSendMessage(null, nextContext, audio)
              }
              onNewChat={handleNewChat}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ChatView;
